import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Chip,
} from "@mui/material";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import BusinessIcon from "@mui/icons-material/Business";
import MessageIcon from "@mui/icons-material/Message";
import ImageIcon from "@mui/icons-material/Image";
import SendIcon from "@mui/icons-material/Send";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { useNavigate } from "react-router-dom";
import {
  getEmpresas,
  getSucursales,
  getWhatsAppTemplates,
  addDifusion,
  getWhatsAppTemplateDetails,
  getVariables,
  getPreviewDestinatarios,
} from "../../services/api";
import showToast from "../../services/toast";

const MAX_HEADER_IMAGE_BYTES = 5 * 1024 * 1024;
const ALLOWED_HEADER_IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png"];

/* ─── Pequeño componente de preview de WhatsApp ─── */
const WhatsAppPreview = ({ templateDetails, variableValues, variableOptions, headerImagePreviewUrl }) => {
  if (!templateDetails) return null;

  const resolveVariable = (index) => {
    const key = variableValues[index];
    if (!key) return `{{${index + 1}}}`;
    const opt = variableOptions.find((o) => o.key === key);
    return opt ? opt.description : key;
  };

  let bodyText = templateDetails.body_text || "";
  if (templateDetails.variables) {
    templateDetails.variables.forEach((_, idx) => {
      bodyText = bodyText.replace(`{{${idx + 1}}}`, `[${resolveVariable(idx)}]`);
    });
  }

  return (
    <div className="wa-phone-shell">
      <div className="wa-status-bar">
        <span>9:41</span>
        <span className="wa-icons">● ● ●</span>
      </div>
      <div className="wa-chat-header">
        <div className="wa-avatar">FF</div>
        <div>
          <div className="wa-contact-name">Fresh Food</div>
          <div className="wa-contact-status">en línea</div>
        </div>
      </div>
      <div className="wa-chat-body">
        <div className="wa-bubble">
          {templateDetails.has_image_header && (
            <div className="wa-bubble-img-slot">
              {headerImagePreviewUrl ? (
                <img src={headerImagePreviewUrl} alt="header" />
              ) : (
                <div className="wa-bubble-img-placeholder">
                  <ImageIcon style={{ fontSize: 28, opacity: 0.4 }} />
                  <span>Imagen de cabecera</span>
                </div>
              )}
            </div>
          )}
          <p className="wa-bubble-body">{bodyText}</p>
          <span className="wa-bubble-time">✓✓</span>
        </div>
      </div>
    </div>
  );
};

/* ─── Componente principal ─── */
const DifusionesEnviar = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [companies, setCompanies] = useState([]);
  const [branches, setBranches] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState("");
  const [selectedBranches, setSelectedBranches] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [templateDetails, setTemplateDetails] = useState(null);
  const [loadingTemplateDetails, setLoadingTemplateDetails] = useState(false);
  const [variableValues, setVariableValues] = useState({});
  const [variableOptions, setVariableOptions] = useState([]);
  const [loadingVariableOptions, setLoadingVariableOptions] = useState(false);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [loadingBranches, setLoadingBranches] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [loadingConfirm, setLoadingConfirm] = useState(false);
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [headerImageFile, setHeaderImageFile] = useState(null);
  const [headerImagePreviewUrl, setHeaderImagePreviewUrl] = useState("");
  const [dragActive, setDragActive] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const [openDestinatariosModal, setOpenDestinatariosModal] = useState(false);
  const previewDebounceRef = useRef(null);
  const previewReqIdRef = useRef(0);

  useEffect(() => {
    return () => {
      if (headerImagePreviewUrl) URL.revokeObjectURL(headerImagePreviewUrl);
    };
  }, [headerImagePreviewUrl]);

  useEffect(() => {
    if (previewDebounceRef.current) {
      clearTimeout(previewDebounceRef.current);
      previewDebounceRef.current = null;
    }
    if (!selectedCompany || selectedBranches.length === 0) {
      previewReqIdRef.current += 1;
      setPreviewData(null);
      setPreviewError("");
      setLoadingPreview(false);
      return;
    }
    setLoadingPreview(true);
    setPreviewError("");
    const branchesSnapshot = [...selectedBranches];
    previewDebounceRef.current = setTimeout(async () => {
      const reqId = ++previewReqIdRef.current;
      try {
        const res = await getPreviewDestinatarios(selectedCompany, branchesSnapshot);
        if (reqId !== previewReqIdRef.current) return;
        setPreviewData(res.data);
        setPreviewError("");
      } catch (e) {
        if (reqId !== previewReqIdRef.current) return;
        setPreviewData(null);
        const msg = e.response?.data?.mensaje;
        setPreviewError(msg ? String(msg) : "No se pudo cargar el preview de destinatarios");
      } finally {
        if (reqId === previewReqIdRef.current) setLoadingPreview(false);
      }
    }, 400);
    return () => {
      if (previewDebounceRef.current) {
        clearTimeout(previewDebounceRef.current);
        previewDebounceRef.current = null;
      }
    };
  }, [selectedCompany, selectedBranches]);

  useEffect(() => {
    const fetchCompanies = async () => {
      setLoadingCompanies(true);
      try {
        const response = await getEmpresas();
        setCompanies(
          response.data.titulares.map((c) => ({
            id: c.ruc_titular,
            nombre: c.nombre_comercial,
          }))
        );
      } catch {
        showToast.error("Error al cargar las empresas");
      } finally {
        setLoadingCompanies(false);
      }
    };
    const fetchTemplates = async () => {
      setLoadingTemplates(true);
      try {
        const response = await getWhatsAppTemplates();
        setTemplates(Array.isArray(response.data.data) ? response.data.data : []);
      } catch {
        showToast.error("Error al cargar las plantillas de WhatsApp");
        setTemplates([]);
      } finally {
        setLoadingTemplates(false);
      }
    };
    const fetchVariableOptions = async () => {
      setLoadingVariableOptions(true);
      try {
        const response = await getVariables("whatsapp");
        setVariableOptions(Array.isArray(response.data) ? response.data : []);
      } catch {
        showToast.error("Error al cargar las opciones de variables");
        setVariableOptions([]);
      } finally {
        setLoadingVariableOptions(false);
      }
    };
    fetchCompanies();
    fetchTemplates();
    fetchVariableOptions();
  }, []);

  useEffect(() => {
    if (!selectedCompany) return;
    const fetchBranches = async () => {
      setLoadingBranches(true);
      try {
        const response = await getSucursales(selectedCompany);
        setBranches(
          response.data.sucursales.map((b) => ({
            id: b.cod_sucursal,
            nombre: b.nom_sucursal,
          }))
        );
      } catch {
        showToast.error("Error al cargar las sucursales");
      } finally {
        setLoadingBranches(false);
      }
    };
    fetchBranches();
  }, [selectedCompany]);

  const handleSendDifusion = (e) => {
    e.preventDefault();
    if (validateForm()) setOpenConfirmationModal(true);
  };

  const handleConfirmSendDifusion = async () => {
    setLoadingConfirm(true);
    try {
      const variablesData = {};
      if (templateDetails?.variables) {
        templateDetails.variables.forEach((_, index) => {
          if (variableValues[index]) variablesData[index + 1] = variableValues[index];
        });
      }
      const difusionData = {
        empresa: selectedCompany,
        sucursales: selectedBranches,
        template_id: selectedTemplate,
        variables: variablesData,
      };
      const imagenParaEnvio = templateDetails?.has_image_header ? headerImageFile : null;
      const response = await addDifusion(difusionData, imagenParaEnvio);
      const { batch_id } = response.data;
      setOpenConfirmationModal(false);
      showToast.success("Difusión creada correctamente");
      navigate(`/app/difusiones/${batch_id}`);
    } catch (error) {
      const msg = error.response?.data?.mensaje || error.message;
      showToast.error(msg ? String(msg) : "Error al enviar la difusión");
    } finally {
      setLoadingConfirm(false);
    }
  };

  const clearHeaderImage = () => {
    if (headerImagePreviewUrl) URL.revokeObjectURL(headerImagePreviewUrl);
    setHeaderImageFile(null);
    setHeaderImagePreviewUrl("");
    setFormErrors((prev) => {
      const next = { ...prev };
      delete next.headerImage;
      return next;
    });
  };

  const assignHeaderImage = (file) => {
    if (!file) return;
    const mime = (file.type || "").toLowerCase();
    if (!ALLOWED_HEADER_IMAGE_TYPES.includes(mime)) {
      showToast.error("Solo se permiten imágenes JPG o PNG.");
      return;
    }
    if (file.size > MAX_HEADER_IMAGE_BYTES) {
      showToast.error("La imagen no puede superar los 5 MB.");
      return;
    }
    if (headerImagePreviewUrl) URL.revokeObjectURL(headerImagePreviewUrl);
    setHeaderImageFile(file);
    setHeaderImagePreviewUrl(URL.createObjectURL(file));
    setFormErrors((prev) => {
      const next = { ...prev };
      delete next.headerImage;
      return next;
    });
  };

  const handleTemplateChange = (templateId) => {
    setSelectedTemplate(templateId);
    setVariableValues({});
    clearHeaderImage();
    if (templateId) {
      fetchTemplateDetails(templateId);
    } else {
      setTemplateDetails(null);
    }
  };

  const fetchTemplateDetails = async (templateId) => {
    setLoadingTemplateDetails(true);
    try {
      const response = await getWhatsAppTemplateDetails(templateId);
      setTemplateDetails(response.data);
    } catch {
      showToast.error("Error al cargar los detalles de la plantilla");
      setTemplateDetails(null);
    } finally {
      setLoadingTemplateDetails(false);
    }
  };

  const handleVariableChange = (index, value) => {
    setVariableValues((prev) => ({ ...prev, [index]: value }));
  };

  const validateForm = () => {
    const errors = {};
    if (!selectedCompany) errors.company = "Seleccione una empresa";
    if (selectedBranches.length === 0) errors.branches = "Seleccione al menos una sucursal";
    if (!selectedTemplate) errors.template = "Seleccione una plantilla";

    if (templateDetails?.variables) {
      const missing = templateDetails.variables
        .map((_, i) => (!variableValues[i] ? i + 1 : null))
        .filter(Boolean);
      if (missing.length > 0)
        errors.variables = `Complete ${missing.length === 1 ? "la variable" : "las variables"} ${missing.join(", ")}`;
    }
    if (templateDetails?.has_image_header && !headerImageFile)
      errors.headerImage = "Adjunte una imagen de cabecera (JPG o PNG, máx. 5 MB)";

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const toggleBranch = (branchId) => {
    setSelectedBranches((prev) =>
      prev.includes(branchId) ? prev.filter((id) => id !== branchId) : [...prev, branchId]
    );
    setFormErrors((prev) => {
      const next = { ...prev };
      delete next.branches;
      return next;
    });
  };

  const selectedTemplateObj = templates.find((t) => t.id === selectedTemplate);
  const totalSteps = templateDetails?.has_image_header ? 3 : 2;
  const completedSteps = [
    selectedCompany && selectedBranches.length > 0,
    selectedTemplate && (!templateDetails?.variables?.length || templateDetails.variables.every((_, i) => variableValues[i])),
    !templateDetails?.has_image_header || !!headerImageFile,
  ].filter(Boolean).length;

  return (
    <div className="de-root">
      {/* Header */}
      <div className="de-header">
        <div className="de-header-left">
          <h1 className="de-title">Nueva difusión</h1>
          <span className="de-subtitle">WhatsApp masivo · {completedSteps}/{totalSteps} pasos completados</span>
        </div>
        <button className="de-link-btn" onClick={() => navigate("/app/difusiones")}>
          Ver historial <KeyboardArrowRightIcon style={{ fontSize: 16, verticalAlign: "middle" }} />
        </button>
      </div>

      {/* Progress bar */}
      <div className="de-progress-track">
        <div
          className="de-progress-fill"
          style={{ width: `${(completedSteps / totalSteps) * 100}%` }}
        />
      </div>

      <form onSubmit={handleSendDifusion}>
        <div className="de-layout">
          {/* ── Columna izquierda: formulario ── */}
          <div className="de-form-col">

            {/* PASO 1 */}
            <section className="de-step">
              <div className="de-step-label">
                <span className={`de-step-num ${selectedCompany && selectedBranches.length > 0 ? "done" : ""}`}>
                  {selectedCompany && selectedBranches.length > 0 ? <CheckCircleOutlineIcon style={{ fontSize: 14 }} /> : "1"}
                </span>
                <BusinessIcon style={{ fontSize: 16, opacity: 0.5 }} />
                <span className="de-step-title">Destino</span>
              </div>

              <div className="de-field-group">
                <label className="de-label">Empresa <span className="de-required">*</span></label>
                {loadingCompanies ? (
                  <div className="de-skeleton" />
                ) : (
                  <div className="de-select-wrapper">
                    <select
                      className={`de-select ${formErrors.company ? "de-error" : ""}`}
                      value={selectedCompany}
                      onChange={(e) => {
                        setSelectedCompany(e.target.value);
                        setSelectedBranches([]);
                        setFormErrors((p) => { const n = { ...p }; delete n.company; return n; });
                      }}
                    >
                      <option value="">Seleccionar empresa…</option>
                      {companies.map((c) => (
                        <option key={c.id} value={c.id}>{c.nombre}</option>
                      ))}
                    </select>
                    <span className="de-select-arrow">▾</span>
                  </div>
                )}
                {formErrors.company && <span className="de-field-error">{formErrors.company}</span>}
              </div>

              {selectedCompany && (
                <div className="de-field-group">
                  <label className="de-label">
                    Sucursales <span className="de-required">*</span>
                    {selectedBranches.length > 0 && (
                      <span className="de-count-badge">{selectedBranches.length} seleccionada{selectedBranches.length > 1 ? "s" : ""}</span>
                    )}
                  </label>
                  {loadingBranches ? (
                    <div className="de-branches-skeleton">
                      {[1, 2, 3].map((i) => <div key={i} className="de-skeleton de-skeleton-chip" />)}
                    </div>
                  ) : (
                    <>
                      <div className="de-branch-list">
                        {branches.map((b) => (
                          <button
                            key={b.id}
                            type="button"
                            className={`de-branch-item ${selectedBranches.includes(b.id) ? "selected" : ""}`}
                            onClick={() => toggleBranch(b.id)}
                          >
                            <span className="de-branch-check">
                              {selectedBranches.includes(b.id) && <CheckCircleOutlineIcon style={{ fontSize: 14 }} />}
                            </span>
                            {b.nombre}
                          </button>
                        ))}
                      </div>
                      {branches.length > 3 && (
                        <button
                          type="button"
                          className="de-select-all-btn"
                          onClick={() =>
                            setSelectedBranches(
                              selectedBranches.length === branches.length ? [] : branches.map((b) => b.id)
                            )
                          }
                        >
                          {selectedBranches.length === branches.length ? "Deseleccionar todas" : "Seleccionar todas"}
                        </button>
                      )}
                    </>
                  )}
                  {formErrors.branches && <span className="de-field-error">{formErrors.branches}</span>}
                </div>
              )}
            </section>

            {/* PASO 2 */}
            <section className="de-step">
              <div className="de-step-label">
                <span className={`de-step-num ${selectedTemplate && templateDetails && (!templateDetails.variables?.length || templateDetails.variables.every((_, i) => variableValues[i])) ? "done" : ""}`}>
                  {selectedTemplate && templateDetails && (!templateDetails.variables?.length || templateDetails.variables.every((_, i) => variableValues[i])) ? <CheckCircleOutlineIcon style={{ fontSize: 14 }} /> : "2"}
                </span>
                <MessageIcon style={{ fontSize: 16, opacity: 0.5 }} />
                <span className="de-step-title">Plantilla de mensaje</span>
              </div>

              <div className="de-field-group">
                <label className="de-label">Plantilla de WhatsApp <span className="de-required">*</span></label>
                {loadingTemplates ? (
                  <div className="de-skeleton" />
                ) : (
                  <div className="de-select-wrapper">
                    <select
                      className={`de-select ${formErrors.template ? "de-error" : ""}`}
                      value={selectedTemplate}
                      onChange={(e) => {
                        handleTemplateChange(e.target.value);
                        setFormErrors((p) => { const n = { ...p }; delete n.template; return n; });
                      }}
                    >
                      <option value="">Seleccionar plantilla…</option>
                      {templates.map((t) => (
                        <option key={t.id} value={t.id} disabled={t.status !== "APPROVED"}>
                          {t.name}{t.status !== "APPROVED" ? ` (${t.status})` : ""}
                        </option>
                      ))}
                    </select>
                    <span className="de-select-arrow">▾</span>
                  </div>
                )}
                {formErrors.template && <span className="de-field-error">{formErrors.template}</span>}
              </div>

              {loadingTemplateDetails && (
                <div className="de-loading-inline">
                  <CircularProgress size={18} thickness={4} />
                  <span>Cargando detalles de la plantilla…</span>
                </div>
              )}

              {!loadingTemplateDetails && templateDetails?.variables?.length > 0 && (
                <div className="de-variables-block">
                  <p className="de-variables-intro">
                    Esta plantilla contiene <strong>{templateDetails.variables.length}</strong> variable{templateDetails.variables.length > 1 ? "s" : ""}.
                    Indique qué campo se usará para cada una.
                  </p>
                  {templateDetails.variables.map((variable, index) => (
                    <div key={index} className="de-field-group">
                      <label className="de-label">
                        Variable <code className="de-var-tag">{`{{${index + 1}}}`}</code>
                        {variable.description && <span className="de-var-hint"> — {variable.description}</span>}
                        <span className="de-required">*</span>
                      </label>
                      <div className="de-select-wrapper">
                        <select
                          className={`de-select ${formErrors.variables ? "de-error" : ""}`}
                          value={variableValues[index] || ""}
                          onChange={(e) => handleVariableChange(index, e.target.value)}
                        >
                          <option value="">Seleccionar campo…</option>
                          {loadingVariableOptions ? (
                            <option disabled>Cargando…</option>
                          ) : (
                            variableOptions.map((opt) => (
                              <option key={opt.key} value={opt.key}>
                                {opt.description} ({opt.key})
                              </option>
                            ))
                          )}
                        </select>
                        <span className="de-select-arrow">▾</span>
                      </div>
                    </div>
                  ))}
                  {formErrors.variables && <span className="de-field-error">{formErrors.variables}</span>}
                </div>
              )}
            </section>

            {/* PASO 3: imagen */}
            {templateDetails?.has_image_header && (
              <section className="de-step">
                <div className="de-step-label">
                  <span className={`de-step-num ${headerImageFile ? "done" : ""}`}>
                    {headerImageFile ? <CheckCircleOutlineIcon style={{ fontSize: 14 }} /> : "3"}
                  </span>
                  <ImageIcon style={{ fontSize: 16, opacity: 0.5 }} />
                  <span className="de-step-title">Imagen de cabecera</span>
                </div>
                <p className="de-step-desc">
                  La misma imagen se enviará a todos los destinatarios. JPG o PNG · máx. 5 MB.
                </p>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png"
                  style={{ display: "none" }}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) assignHeaderImage(f);
                    e.target.value = "";
                  }}
                />

                {!headerImagePreviewUrl ? (
                  <div
                    role="button"
                    tabIndex={0}
                    className={`de-dropzone ${dragActive ? "active" : ""} ${formErrors.headerImage ? "error" : ""}`}
                    onClick={() => fileInputRef.current?.click()}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        fileInputRef.current?.click();
                      }
                    }}
                    onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
                    onDragLeave={() => setDragActive(false)}
                    onDrop={(e) => {
                      e.preventDefault();
                      setDragActive(false);
                      const f = e.dataTransfer.files?.[0];
                      if (f) assignHeaderImage(f);
                    }}
                  >
                    <AddPhotoAlternateIcon className="de-dropzone-icon" />
                    <span className="de-dropzone-primary">Arrastra una imagen o haz clic para seleccionar</span>
                    <span className="de-dropzone-secondary">JPG · PNG · máx. 5 MB</span>
                  </div>
                ) : (
                  <div className="de-img-preview-wrap">
                    <img
                      src={headerImagePreviewUrl}
                      alt="Vista previa cabecera"
                      className="de-img-preview"
                    />
                    <div className="de-img-actions">
                      <button type="button" className="de-img-change-btn" onClick={() => fileInputRef.current?.click()}>
                        Cambiar imagen
                      </button>
                      <button type="button" className="de-img-remove-btn" onClick={clearHeaderImage}>
                        <CloseIcon style={{ fontSize: 14 }} /> Eliminar
                      </button>
                    </div>
                  </div>
                )}
                {formErrors.headerImage && <span className="de-field-error">{formErrors.headerImage}</span>}
              </section>
            )}

            {/* CTA */}
            <div className="de-cta-row">
              <button type="submit" className="de-submit-btn" disabled={loadingConfirm}>
                {loadingConfirm ? (
                  <CircularProgress size={18} color="inherit" thickness={4} />
                ) : (
                  <>
                    <SendIcon style={{ fontSize: 16 }} />
                    Enviar difusión
                  </>
                )}
              </button>
            </div>
          </div>

          {/* ── Columna derecha: preview ── */}
          <div className="de-preview-col">
            <div className="de-preview-sticky">
              <p className="de-preview-label">Vista previa del mensaje</p>
              {selectedTemplate ? (
                loadingTemplateDetails ? (
                  <div className="de-loading-inline">
                    <CircularProgress size={18} thickness={4} />
                    <span>Cargando…</span>
                  </div>
                ) : (
                  <WhatsAppPreview
                    templateDetails={templateDetails}
                    variableValues={variableValues}
                    variableOptions={variableOptions}
                    headerImagePreviewUrl={headerImagePreviewUrl}
                  />
                )
              ) : (
                <div className="de-preview-empty">
                  <MessageIcon style={{ fontSize: 32, opacity: 0.2 }} />
                  <span>Selecciona una plantilla para ver la vista previa</span>
                </div>
              )}

              {/* Resumen de destino */}
              {(selectedCompany || selectedBranches.length > 0) && (
                <div className="de-dest-summary">
                  {selectedCompany && (
                    <div className="de-dest-row">
                      <span className="de-dest-key">Empresa</span>
                      <span className="de-dest-val">
                        {companies.find((c) => c.id === selectedCompany)?.nombre || selectedCompany}
                      </span>
                    </div>
                  )}
                  {selectedBranches.length > 0 && (
                    <div className="de-dest-row de-dest-row--chips">
                      <span className="de-dest-key">Sucursales</span>
                      <div className="de-dest-chips">
                        {selectedBranches.map((id) => {
                          const b = branches.find((br) => br.id === id);
                          return b ? (
                            <Chip
                              key={id}
                              label={b.nombre}
                              size="small"
                              onDelete={() => toggleBranch(id)}
                              sx={{
                                fontSize: "11px",
                                height: "22px",
                                backgroundColor: "#f0f4e8",
                                color: "#4a5e10",
                                "& .MuiChip-deleteIcon": { fontSize: "14px", color: "#7a9a20" },
                              }}
                            />
                          ) : null;
                        })}
                      </div>
                    </div>
                  )}
                  {selectedTemplateObj && (
                    <div className="de-dest-row">
                      <span className="de-dest-key">Plantilla</span>
                      <span className="de-dest-val">{selectedTemplateObj.name}</span>
                    </div>
                  )}
                </div>
              )}

              {selectedCompany && selectedBranches.length > 0 && (
                <div className="de-recipient-preview">
                  <p className="de-recipient-preview-label">Destinatarios estimados</p>
                  {loadingPreview && (
                    <div className="de-recipient-skeleton-wrap">
                      <div className="de-recipient-skeleton-line" />
                      <div className="de-recipient-skeleton-line short" />
                    </div>
                  )}
                  {!loadingPreview && previewError && (
                    <p className="de-recipient-error">{previewError}</p>
                  )}
                  {!loadingPreview && !previewError && previewData && (
                    <>
                      <div className="de-recipient-count-wrap">
                        <span className="de-recipient-count">{previewData.total}</span>
                        <span className="de-recipient-count-caption">
                          {previewData.total === 1
                            ? "usuario con teléfono registrado"
                            : "usuarios con teléfono registrado"}
                        </span>
                      </div>
                      {previewData.total === 0 ? (
                        <p className="de-recipient-empty">
                          No se encontraron usuarios con teléfono en las sucursales seleccionadas.
                        </p>
                      ) : (
                        <button
                          type="button"
                          className="de-recipient-list-btn"
                          onClick={() => setOpenDestinatariosModal(true)}
                        >
                          Ver lista completa
                          <KeyboardArrowRightIcon style={{ fontSize: 16 }} />
                        </button>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </form>

      {/* ── Modal lista de destinatarios ── */}
      <Dialog
        open={openDestinatariosModal}
        onClose={() => setOpenDestinatariosModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: "12px" } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1 }}>
          <span style={{ fontWeight: 700, fontSize: "1rem" }}>
            Destinatarios ({previewData?.total ?? 0})
          </span>
          <IconButton size="small" onClick={() => setOpenDestinatariosModal(false)} aria-label="Cerrar">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <div className="de-recipient-table-wrap">
            <table className="de-recipient-table">
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Sucursal</th>
                </tr>
              </thead>
              <tbody>
                {(previewData?.destinatarios || []).map((row, idx) => (
                  <tr key={`${row.sucursal}-${row.nombre}-${idx}`}>
                    <td>{row.nombre || "—"}</td>
                    <td>
                      {branches.find((b) => b.id === row.sucursal)?.nombre || row.sucursal || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5 }}>
          <Button variant="outlined" onClick={() => setOpenDestinatariosModal(false)} sx={{ textTransform: "none", borderRadius: "8px" }}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Modal de confirmación ── */}
      <Dialog
        open={openConfirmationModal}
        onClose={() => !loadingConfirm && setOpenConfirmationModal(false)}
        PaperProps={{ sx: { borderRadius: "12px", p: 1, minWidth: 360 } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1 }}>
          <span style={{ fontWeight: 700, fontSize: "1rem" }}>Confirmar envío</span>
          {!loadingConfirm && (
            <IconButton size="small" onClick={() => setOpenConfirmationModal(false)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </DialogTitle>
        <DialogContent>
          <div className="de-confirm-summary">
            <div className="de-confirm-row">
              <span className="de-confirm-key">Empresa</span>
              <span className="de-confirm-val">
                {companies.find((c) => c.id === selectedCompany)?.nombre || selectedCompany}
              </span>
            </div>
            <div className="de-confirm-row">
              <span className="de-confirm-key">Sucursales</span>
              <span className="de-confirm-val">{selectedBranches.length} seleccionada{selectedBranches.length !== 1 ? "s" : ""}</span>
            </div>
            {selectedTemplateObj && (
              <div className="de-confirm-row">
                <span className="de-confirm-key">Plantilla</span>
                <span className="de-confirm-val">{selectedTemplateObj.name}</span>
              </div>
            )}
          </div>
          {templateDetails?.has_image_header && headerImagePreviewUrl && (
            <div className="de-confirm-img">
              <span className="de-confirm-img-label">Imagen de cabecera</span>
              <img src={headerImagePreviewUrl} alt="" />
            </div>
          )}
          <p className="de-confirm-warning">
            Esta acción enviará mensajes de WhatsApp a todos los contactos de las sucursales seleccionadas.
          </p>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => setOpenConfirmationModal(false)}
            disabled={loadingConfirm}
            sx={{ borderRadius: "8px", textTransform: "none", borderColor: "#ddd", color: "#555" }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmSendDifusion}
            disabled={loadingConfirm}
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              backgroundColor: "#9DB721",
              "&:hover": { backgroundColor: "#8aa61d" },
              minWidth: 120,
            }}
          >
            {loadingConfirm ? <CircularProgress size={18} color="inherit" thickness={4} /> : "Confirmar envío"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default DifusionesEnviar;
